import { ComponentProps } from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

import { OpInput } from 'new-components/DLS/OpInput/OpInput';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { useOpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';

// These values are from Helium's `passwordPolicy` table

const minimumLength = 15;
const numLowercaseRequired = 1;
const numUppercaseRequired = 1;
const numDigitRequired = 1;

interface OpFormPasswordInput
  extends Pick<
      ComponentProps<typeof OpForm.Item>,
      | 'name'
      | 'label'
      | 'tooltip'
      | 'rules'
      | 'validateTrigger'
      | 'validateStatus' // Temp: For SUIR wrapped version
      | 'help' // Temp: For SUIR wrapped version
      | 'required' // Temp: For SUIR wrapped version
    >,
    Omit<ComponentProps<typeof OpInput.Password>, 'name' | 'required'> {
  formItemClassName?: string;
  isLoading?: boolean;
  enforcePolicy?: boolean;
}

export const OpFormPasswordInput = ({
  // Form Item props
  formItemClassName,
  name,
  label,
  tooltip,
  rules,
  validateTrigger,
  required, // TEMP: For old form component validation
  validateStatus, // TEMP: For old form component validation
  help, // TEMP: For old form component validation

  enforcePolicy,

  // For skeleton loading state
  isLoading,

  // Wrapped element props
  disabled,

  testId,

  ...elementProps
}: OpFormPasswordInput) => {
  /** The Form is wrapped in a provider that passes the loading and readOnly
   * contexts to the component */
  const { isDataLoading, isReadOnly } = useOpFormContext();
  const { t } = useTranslation();

  const uuid = uuidv4();
  const opPasswordInputId = `op-password-input-${uuid}`;

  let inputRules = rules;

  if (enforcePolicy) {
    inputRules = [
      ...(rules ?? []),
      {
        min: minimumLength,
        message: t(`Password must have at least ${minimumLength} characters`),
      },
      {
        pattern: new RegExp(`[a-z]{${numLowercaseRequired},}`),
        message: t(
          `Password must have at least ${numLowercaseRequired} lowercase letter`,
        ),
      },
      {
        pattern: new RegExp(`[A-Z]{${numUppercaseRequired},}`),
        message: t(
          `Password must have at least ${numUppercaseRequired} uppercase letter`,
        ),
      },
      {
        pattern: new RegExp(`[0-9]{${numDigitRequired},}`),
        message: t(`Password must have at least ${numDigitRequired} digit(s)`),
      },
    ];
  }

  return (
    <OpForm.Item
      className={clsx('op-form-password-input', formItemClassName)}
      name={name}
      label={label}
      tooltip={tooltip}
      rules={inputRules}
      validateTrigger={validateTrigger}
      required={required} // TEMP: For old form component validation
      validateStatus={validateStatus} // TEMP: For old form component validation
      help={help} // TEMP: For old form component validation
      testId={testId ? `${testId}-op-form-item` : undefined}
      {...(!name && { htmlFor: opPasswordInputId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
    >
      {isDataLoading || isLoading ? (
        /** A placeholder in the shape of the component
         * so we can immediately render the structure of the form while
         * data is loading */
        <OpSkeleton.Input active block />
      ) : (
        /** The actual component that is rendered when we have the data */
        <OpInput.Password
          disabled={isReadOnly || disabled}
          testId={testId}
          {...(!name && { id: opPasswordInputId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
          {...elementProps}
        />
      )}
    </OpForm.Item>
  );
};
